import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import {
  getPatientInteractions,
  PATIENT_INTERACTIONS_DEFAULT_LIMIT,
  PatientInteraction,
} from '../../services/patientInteractionsService';

/**
 * The current user's patient interaction history for the active pharmacy
 * (GP-1508). Backs the topbar history menu.
 *
 * `enabled` is driven by the menu being open: the list is only interesting when
 * someone is looking at it, and fetching it on every page would put a request
 * behind every navigation for a menu most of the time nobody opens.
 * `staleTime: 0` so opening the menu after doing work shows that work.
 */
export function usePatientInteractions(options?: { enabled?: boolean; limit?: number }) {
  // The Auth0 subject — the same `sub` the server scopes the list by.
  const userId = useAuth0().user?.sub;
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);
  const pharmacyId = activePharmacy?.id;
  const limit = options?.limit ?? PATIENT_INTERACTIONS_DEFAULT_LIMIT;

  return useQuery<PatientInteraction[]>({
    // userId is part of the key (PRX-87): a second person signing in on this
    // workstation never reads the first person's cached list while their own
    // loads. pharmacyId is too: switching pharmacy shows that pharmacy's list
    // rather than a cached one from the pharmacy you just left.
    queryKey: ['patient-interactions', userId, pharmacyId, limit],
    queryFn: () => getPatientInteractions(pharmacyId as string, limit),
    // Disabled until both are known, never run under an undefined key.
    enabled: !!userId && !!pharmacyId && options?.enabled !== false,
    staleTime: 0,
  });
}
